import React, { useState, useEffect } from 'react';
import Chart from '../Chart/Chart';
import Results from '../Results/Results';
import Scenarios from '../Scenarios/Scenarios';
import Milestones from '../Milestones/Milestones';
import RetirementPlanner from '../RetirementPlanner/RetirementPlanner';
import ExportData from '../Export/ExportData';
import Tooltip from '../UI/Tooltip';
import HelpButton from '../UI/HelpButton';
import useCalculator from '../../hooks/useCalculator';
import { useData } from '../../context/DataContext';
import './Calculator.css';

const Calculator = ({ initialStep }) => {
  const [formData, setFormData] = useState({
    initialCapital: 1000,
    monthlyContribution: 100,
    interestRate: 0.8, // 0.8% ao mês
    period: 12, // meses
    taxRate: 0, // alíquota de imposto
  });
  
  const [activeTab, setActiveTab] = useState(initialStep || 'calculator');
  const [isCalculated, setIsCalculated] = useState(false);
  const [showStepsGuide, setShowStepsGuide] = useState(true);
  
  // Utilizando o hook personalizado para cálculos
  const { results, calculateResults, calculateInvestmentScenarios, calculateMilestones } = useCalculator();
  
  // Acessando o contexto de dados para salvar resultados
  const { 
    saveCalculatorData, 
    saveScenariosData, 
    saveMilestonesData, 
    saveRetirementData
  } = useData();
  
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: parseFloat(value) || 0,
    });
  };
  
  const handleSubmit = (e) => {
    e.preventDefault();
    const calculatedResults = calculateResults(formData);
    setIsCalculated(true);
    
    // Salvando dados no contexto para exportação posterior
    saveCalculatorData({
      ...formData,
      results: calculatedResults
    });
    
    // Rola para os resultados após o cálculo (experiência de usuário melhorada)
    setTimeout(() => {
      const resultsElement = document.querySelector('.results-container');
      if (resultsElement) {
        resultsElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 100);
  };
  
  // Função para ir para a próxima etapa
  const goToNextStep = () => {
    if (activeTab === 'calculator' && isCalculated) {
      setActiveTab('scenarios');
    } else if (activeTab === 'scenarios') {
      setActiveTab('milestones');
    } else if (activeTab === 'milestones') {
      setActiveTab('retirement');
    } else if (activeTab === 'retirement') {
      setActiveTab('export');
    }
  };
  
  // Textos explicativos para os tooltips
  const tooltips = {
    initialCapital: "O valor inicial que você investirá. Este é o capital com o qual você começa seu investimento.",
    monthlyContribution: "Quanto você pretende investir mensalmente de forma recorrente.",
    interestRate: "A taxa de juros mensal do seu investimento. Por exemplo, para 1% ao mês, digite 1.",
    period: "Por quanto tempo você manterá seu investimento, em meses. Para ver resultados anuais, use múltiplos de 12.",
    taxRate: "A alíquota de imposto aplicada sobre o rendimento (opcional). No Brasil varia de 15% a 22,5% para investimentos em renda fixa.",
  };
  
  // Fecha o guia de etapas
  const closeStepsGuide = () => {
    setShowStepsGuide(false);
  };
  
  // Salvar dados de cenários
  const handleSaveScenarios = (scenariosData) => {
    // Calcula os cenários usando os dados fornecidos
    const scenariosResults = calculateInvestmentScenarios(formData, scenariosData);
    saveScenariosData(scenariosResults);
  };
  
  // Salvar dados de marcos
  const handleSaveMilestones = (milestonesData) => {
    // Calcula os marcos usando os dados fornecidos
    const milestonesResults = calculateMilestones(formData, milestonesData);
    saveMilestonesData(milestonesResults);
  };
  
  // Salvar dados de aposentadoria
  const handleSaveRetirement = (retirementData) => {
    saveRetirementData(retirementData);
  };
  
  // Atualiza a tab ativa quando o initialStep é fornecido via props
  useEffect(() => {
    if (initialStep) {
      setActiveTab(initialStep);
    }
  }, [initialStep]);
  
  // Verificar se há dados para mostrar na etapa de exportação
  const canShowExport = isCalculated;
  
  return (
    <div className="calculator-container">
      {/* Guia de processo em etapas - aparece somente quando necessário */}
      {showStepsGuide && !isCalculated && (
        <div className="steps-guide">
          <div className="steps-guide-content">
            <div className="steps-guide-header">
              <h3>Sua jornada de investimento tem 5 etapas</h3>
              <button className="steps-guide-close" onClick={closeStepsGuide} aria-label="Fechar guia">×</button>
            </div>
            <div className="steps-guide-steps">
              <div className="guide-step active">
                <div className="step-number">1</div>
                <div className="step-content">
                  <div className="step-title">Calculadora</div>
                  <div className="step-description">Simule seus investimentos com juros compostos</div>
                </div>
              </div>
              <div className="guide-step">
                <div className="step-number">2</div>
                <div className="step-content">
                  <div className="step-title">Cenários</div>
                  <div className="step-description">Compare diferentes taxas de juros</div>
                </div>
              </div>
              <div className="guide-step">
                <div className="step-number">3</div>
                <div className="step-content">
                  <div className="step-title">Marcos</div>
                  <div className="step-description">Descubra quanto tempo para atingir seus objetivos</div>
                </div>
              </div>
              <div className="guide-step">
                <div className="step-number">4</div>
                <div className="step-content">
                  <div className="step-title">Aposentadoria</div>
                  <div className="step-description">Planeje sua independência financeira</div>
                </div>
              </div>
              <div className="guide-step">
                <div className="step-number">5</div>
                <div className="step-content">
                  <div className="step-title">Exportar</div>
                  <div className="step-description">Salve ou imprima seus resultados</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Abas da calculadora */}
      <div className="calculator-tabs">
        <button 
          className={`calculator-tab ${activeTab === 'calculator' ? 'active' : ''}`}
          onClick={() => setActiveTab('calculator')}
        >
          <span className="tab-help">
            <HelpButton 
              title="Calculadora de Juros Compostos" 
              content="Nesta etapa, você pode calcular o crescimento do seu investimento usando o poder dos juros compostos. Basta inserir seus valores e visualizar como seu dinheiro crescerá ao longo do tempo."
            />
          </span>
          <div className="tab-content">
            <span className="tab-number">1</span>
            <span className="tab-text">Calculadora</span>
          </div>
        </button>
        
        <button 
          className={`calculator-tab ${activeTab === 'scenarios' ? 'active' : ''} ${!isCalculated ? 'disabled' : ''}`}
          onClick={() => isCalculated && setActiveTab('scenarios')}
          disabled={!isCalculated}
        >
          <span className="tab-help">
            <HelpButton 
              title="Comparação de Cenários" 
              content="Compare diferentes cenários de investimento alterando as taxas de juros. Veja como diferentes rendimentos afetam seus resultados a longo prazo."
            />
          </span>
          <div className="tab-content">
            <span className="tab-number">2</span>
            <span className="tab-text">Cenários</span>
          </div>
        </button>
        
        <button 
          className={`calculator-tab ${activeTab === 'milestones' ? 'active' : ''} ${!isCalculated ? 'disabled' : ''}`}
          onClick={() => isCalculated && setActiveTab('milestones')}
          disabled={!isCalculated}
        >
          <span className="tab-help">
            <HelpButton 
              title="Marcos Financeiros" 
              content="Defina objetivos financeiros e descubra quanto tempo você levará para alcançá-los com seu plano de investimento atual."
            />
          </span>
          <div className="tab-content">
            <span className="tab-number">3</span>
            <span className="tab-text">Marcos</span>
          </div>
        </button>
        
        <button 
          className={`calculator-tab ${activeTab === 'retirement' ? 'active' : ''} ${!isCalculated ? 'disabled' : ''}`}
          onClick={() => isCalculated && setActiveTab('retirement')}
          disabled={!isCalculated}
        >
          <span className="tab-help">
            <HelpButton 
              title="Planejamento de Aposentadoria" 
              content="Estime quanto você precisará para se aposentar confortavelmente e calcule quanto tempo levará para atingir sua independência financeira."
            />
          </span>
          <div className="tab-content">
            <span className="tab-number">4</span>
            <span className="tab-text">Aposentadoria</span>
          </div>
        </button>
        
        <button 
          className={`calculator-tab ${activeTab === 'export' ? 'active' : ''} ${!canShowExport ? 'disabled' : ''}`}
          onClick={() => canShowExport && setActiveTab('export')}
          disabled={!canShowExport}
        >
          <span className="tab-help">
            <HelpButton 
              title="Exportar Resultados" 
              content="Salve seus resultados em diferentes formatos para referência futura ou compartilhamento."
            />
          </span>
          <div className="tab-content">
            <span className="tab-number">5</span>
            <span className="tab-text">Exportar</span>
          </div>
        </button>
      </div>
      
      {/* Conteúdo da calculadora */}
      <div className="calculator-content">
        {/* Primeira etapa: Calculadora básica */}
        {activeTab === 'calculator' && (
          <div>
            <form onSubmit={handleSubmit} className="calculator-form">
              <h2>Calculadora de Juros Compostos</h2>
              <p className="calculator-intro">
                Descubra como seu dinheiro pode crescer com o poder dos juros compostos. 
                Insira seus valores e veja como seu investimento evolui ao longo do tempo.
              </p>
              
              <div className="form-grid">
                <div className="form-group">
                  <label htmlFor="initialCapital">
                    Capital Inicial (R$)
                    <Tooltip content={tooltips.initialCapital} />
                  </label>
                  <input
                    type="number"
                    id="initialCapital"
                    name="initialCapital"
                    value={formData.initialCapital}
                    onChange={handleInputChange}
                    min="0"
                    step="100"
                    required
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="monthlyContribution">
                    Aporte Mensal (R$)
                    <Tooltip content={tooltips.monthlyContribution} />
                  </label>
                  <input
                    type="number"
                    id="monthlyContribution"
                    name="monthlyContribution"
                    value={formData.monthlyContribution}
                    onChange={handleInputChange}
                    min="0"
                    step="10"
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="interestRate">
                    Taxa de Juros (% ao mês)
                    <Tooltip content={tooltips.interestRate} />
                  </label>
                  <input
                    type="number"
                    id="interestRate"
                    name="interestRate"
                    value={formData.interestRate}
                    onChange={handleInputChange}
                    min="0"
                    step="0.1"
                    max="10"
                    required
                  />
                  <div className="interest-rate-buttons">
                    <button 
                      type="button" 
                      onClick={() => setFormData({...formData, interestRate: 0.4})}
                      className="interest-preset-button"
                    >
                      Poupança (0.4%)
                    </button>
                    <button 
                      type="button" 
                      onClick={() => setFormData({...formData, interestRate: 0.9})}
                      className="interest-preset-button"
                    >
                      CDB (0.9%)
                    </button>
                    <button 
                      type="button" 
                      onClick={() => setFormData({...formData, interestRate: 1.0})}
                      className="interest-preset-button"
                    >
                      Tesouro SELIC (1.0%)
                    </button>
                    <button 
                      type="button" 
                      onClick={() => setFormData({...formData, interestRate: 1.2})}
                      className="interest-preset-button"
                    >
                      LCI/LCA (1.2%)
                    </button>
                  </div>
                </div>
                
                <div className="form-group">
                  <label htmlFor="period">
                    Período (meses)
                    <Tooltip content={tooltips.period} />
                  </label>
                  <input
                    type="number"
                    id="period"
                    name="period"
                    value={formData.period}
                    onChange={handleInputChange}
                    min="1"
                    required
                  />
                  <div className="period-buttons">
                    <button 
                      type="button" 
                      onClick={() => setFormData({...formData, period: 12})}
                      className="period-preset-button"
                    >
                      1 ano
                    </button>
                    <button 
                      type="button" 
                      onClick={() => setFormData({...formData, period: 60})}
                      className="period-preset-button"
                    >
                      5 anos
                    </button>
                    <button 
                      type="button" 
                      onClick={() => setFormData({...formData, period: 120})}
                      className="period-preset-button"
                    >
                      10 anos
                    </button>
                  </div>
                </div>
                
                <div className="form-group">
                  <label htmlFor="taxRate">
                    Imposto de Renda (%)
                    <Tooltip content={tooltips.taxRate} />
                  </label>
                  <input
                    type="number"
                    id="taxRate"
                    name="taxRate"
                    value={formData.taxRate}
                    onChange={handleInputChange}
                    min="0"
                    max="100"
                    step="0.5"
                  />
                  <div className="tax-rate-buttons">
                    <button 
                      type="button" 
                      onClick={() => setFormData({...formData, taxRate: 22.5})}
                      className="tax-preset-button"
                    >
                      Até 180 dias (22.5%)
                    </button>
                    <button 
                      type="button" 
                      onClick={() => setFormData({...formData, taxRate: 20})}
                      className="tax-preset-button"
                    >
                      181-360 dias (20%)
                    </button>
                    <button 
                      type="button" 
                      onClick={() => setFormData({...formData, taxRate: 17.5})}
                      className="tax-preset-button"
                    >
                      361-720 dias (17.5%)
                    </button>
                    <button 
                      type="button" 
                      onClick={() => setFormData({...formData, taxRate: 15})}
                      className="tax-preset-button"
                    >
                      Acima de 720 dias (15%)
                    </button>
                  </div>
                </div>
              </div>
              
              <button type="submit" className="calculate-button">
                <span className="button-icon">📊</span>
                <span>Calcular Investimento</span>
              </button>
            </form>
            
            {/* Resultados */}
            {isCalculated && (
              <div className="after-calculation">
                <Results 
                  results={results.summary} 
                  formData={formData} 
                />
                
                {results.chartData && results.chartData.length > 0 && (
                  <Chart 
                    data={results.chartData}
                    hasMonthlyContribution={formData.monthlyContribution > 0}
                  />
                )}
                
                <div className="next-step-container">
                  <button className="next-step-button" onClick={goToNextStep}>
                    <span>Avançar para Cenários</span>
                    <span className="next-arrow">→</span>
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
        
        {/* Segunda etapa: Comparação de Cenários */}
        {activeTab === 'scenarios' && (
          <div>
            <Scenarios 
              initialData={formData} 
              onSaveData={handleSaveScenarios}
            />
            
            <div className="next-step-container">
              <button className="next-step-button" onClick={goToNextStep}>
                <span>Avançar para Marcos</span>
                <span className="next-arrow">→</span>
              </button>
            </div>
          </div>
        )}
        
        {/* Terceira etapa: Marcos financeiros */}
        {activeTab === 'milestones' && (
          <div>
            <Milestones 
              initialData={formData}
              onSaveData={handleSaveMilestones}
            />
            
            <div className="next-step-container">
              <button className="next-step-button" onClick={goToNextStep}>
                <span>Avançar para Aposentadoria</span>
                <span className="next-arrow">→</span>
              </button>
            </div>
          </div>
        )}
        
        {/* Quarta etapa: Planejamento de aposentadoria */}
        {activeTab === 'retirement' && (
          <div>
            <RetirementPlanner 
              initialFormData={formData}
              onSaveData={handleSaveRetirement}
            />
            
            <div className="next-step-container">
              <button className="next-step-button" onClick={goToNextStep}>
                <span>Avançar para Exportar</span>
                <span className="next-arrow">→</span>
              </button>
            </div>
          </div>
        )}
        
        {/* Quinta etapa: Exportar dados */}
        {activeTab === 'export' && (
          <div>
            <ExportData />
          </div>
        )}
      </div>
    </div>
  );
};

export default Calculator;